/**
 * @description 埋点管理器 - 集中配置管理方案
 * @author Cursor
 * @date 2023-05-19
 */

import { IBurialPointStrategy, IQueuedEvent } from './types';
import { ConfigurationManager } from './configuration-manager';
import { BurialPointFactory } from './burial-factory';
import { initializeRequestInterceptor } from './utils/request-interceptor';

// write by cursor
/**
 * 埋点管理器 - 单例模式
 */
export class BurialPointManager {
  private static instance: BurialPointManager;
  private strategy: IBurialPointStrategy | null = null;
  private initialized = false;
  private platform: string = 'iam'; // 默认平台

  // 事件队列
  private queuedEvents: IQueuedEvent[] = [];

  private constructor() {
    // 私有构造函数，防止外部实例化
  }

  // 获取单例实例
  public static getInstance(): BurialPointManager {
    if (!BurialPointManager.instance) {
      BurialPointManager.instance = new BurialPointManager();
    }
    return BurialPointManager.instance;
  }

  // 设置平台
  public setPlatform(platform: string): void {
    this.platform = platform;
  }

  // 初始化埋点系统
  public async initialize(endpoint?: string, env?: string): Promise<void> {
    if (this.initialized) return;

    try {
      // 初始化全局请求拦截器
      initializeRequestInterceptor();

      // 获取当前区域和环境
      const environment = ConfigurationManager.getCurrentEnvironment(env);

      // 获取配置
      const strategyConfig = ConfigurationManager.getConfigByPlatform(
        this.platform,
        endpoint
      );

      // 创建埋点策略
      this.strategy = BurialPointFactory.createStrategy(
        strategyConfig,
        environment
      );

      // 初始化策略
      await this.strategy.init();

      // 标记为已初始化
      this.initialized = true;

      // 处理队列中的事件
      this.processQueuedEvents();

      console.log(`initialized: platform=${this.platform}, environment=${environment}`);
    } catch (error) {
      console.error("initialize failed:", error);
    }
  }

  // 处理队列中的事件
  private processQueuedEvents(): void {
    if (!this.strategy || !this.initialized) return;

    while (this.queuedEvents.length > 0) {
      const event = this.queuedEvents.shift();
      if (!event) continue;

      switch (event.type) {
        case 'event':
          this.strategy.sendEvent(...(event.args as [string, string, Record<string, any>?]));
          break;
        case 'startTrace':
          if (this.strategy.startTrace) {
            this.strategy.startTrace(...(event.args as [string]));
          }
          break;
        case 'stopTrace':
          if (this.strategy.stopTrace) {
            this.strategy.stopTrace(...(event.args as [string]));
          }
          break;
      }
    }
  }

  // 发送事件
  public sendEvent(eventType: string, eventName: string, params?: Record<string, any>): void {
    if (!this.initialized || !this.strategy) {
      // 未初始化时，将事件加入队列
      this.queuedEvents.push({
        type: 'event',
        args: [eventType, eventName, params]
      });
      return;
    }

    this.strategy.sendEvent(eventType, eventName, params);
  }

  // 开始性能追踪
  public startTrace(traceName: string): void {
    if (!this.initialized || !this.strategy || !this.strategy.startTrace) {
      // 未初始化时，将事件加入队列
      this.queuedEvents.push({
        type: 'startTrace',
        args: [traceName]
      });
      return;
    }

    this.strategy.startTrace(traceName);
  }

  // 结束性能追踪
  public stopTrace(traceName: string): void {
    if (!this.initialized || !this.strategy || !this.strategy.stopTrace) {
      // 未初始化时，将事件加入队列
      this.queuedEvents.push({
        type: 'stopTrace',
        args: [traceName]
      });
      return;
    }

    this.strategy.stopTrace(traceName);
  }
}
